import {
    ClientStatus,
    IClient,
    IGDriveFile,
    InsuranceCard,
} from "intake_shared/src/types";
import * as React from "react";
import { firebaseAuth } from "../../../../../../utils/firestore";
import { TUpdateClientByStatus } from "../../../clientUiTypes";
import { uniqBy } from "lodash";
import { Card } from "../../../../bits/Card/Card";
import { Button } from "../../../../bits/Button/Button";
import CancelIcon from "../../../../icons/Cancel";
import GDriveSelect from "../../../../bits/GDriveSelect/GDriveSelect";
import styles from "./InsuranceCardUpload.module.scss";

interface InsuranceCardUploadProps {
    client: IClient;
    updateClientByStatus: TUpdateClientByStatus;
    allowUpload?: boolean;
}

export default function InsuranceCardUpload({
    client,
    updateClientByStatus,
    allowUpload = true,
}: InsuranceCardUploadProps) {
    const getInsurancePhotos = () =>
        client[ClientStatus.INS_FOLLOWUP]?.photo_links || [];
    const addInsurancePhotos = (files: IGDriveFile[]) => {
        let cards = getInsurancePhotos();
        cards = uniqBy(
            cards.concat([
                ...files.map((f) => {
                    return {
                        id: f.id,
                        name: f.name,
                        url: f.url,
                    };
                }),
            ]),
            "id"
        );
        updateClientByStatus(ClientStatus.INS_FOLLOWUP, "photo_links", cards);
    };

    const removeInsurancePhotos = (file: InsuranceCard) => {
        const cards = getInsurancePhotos();
        updateClientByStatus(
            ClientStatus.INS_FOLLOWUP,
            "photo_links",
            cards.filter((c) => c.id !== file.id)
        );
    };
    const openInsuranceCard = async (
        event: React.MouseEvent<HTMLAnchorElement>,
        url: string
    ) => {
        const bucketName = process.env.REACT_APP_INSURANCE_CARD_BUCKET;
        if (!bucketName) return;

        let cardUrl: URL;
        try {
            cardUrl = new URL(url);
        } catch {
            return;
        }

        const prefix = `/${bucketName}/insurance_card_uploads/`;
        if (
            cardUrl.hostname !== "storage.googleapis.com" ||
            !cardUrl.pathname.startsWith(prefix) ||
            cardUrl.search ||
            cardUrl.hash
        )
            return;

        event.preventDefault();
        const cardTab = window.open("", "_blank");
        if (cardTab) cardTab.opener = null;
        try {
            const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID;
            const user = firebaseAuth.currentUser;
            if (!projectId || !user) {
                throw new Error("Intake authentication is required");
            }

            const objectPath = decodeURIComponent(
                cardUrl.pathname.slice(`/${bucketName}/`.length)
            );
            const functionBaseUrl =
                window.location.hostname === "localhost"
                    ? `http://localhost:5001/${projectId}/us-east1`
                    : `https://us-east1-${projectId}.cloudfunctions.net`;
            const response = await fetch(
                `${functionBaseUrl}/httpIntakeServerApiV2/client/insurance-card?path=${encodeURIComponent(
                    objectPath
                )}`,
                {
                    headers: {
                        Authorization: `Bearer ${await user.getIdToken()}`,
                    },
                }
            );
            if (!response.ok) {
                throw new Error(`Insurance card request failed: ${response.status}`);
            }

            const card = await response.blob();
            const objectUrl = URL.createObjectURL(card);
            if (cardTab) {
                cardTab.location.href = objectUrl;
            } else {
                window.location.href = objectUrl;
            }
            window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
        } catch (error) {
            cardTab?.close();
            console.error("Could not open insurance card", error);
            window.alert("Could not open insurance card");
        }
    };
    return (
        <Card>
            <p className={styles.title}>INSURANCE CARD</p>
            <div className="gridTwo">
                {allowUpload && (
                    <GDriveSelect
                        buttonLabel="Click to upload"
                        onSelectFiles={(files) => addInsurancePhotos(files)}
                    />
                )}
                {client[ClientStatus.INS_FOLLOWUP]?.photo_links &&
                    client[ClientStatus.INS_FOLLOWUP]?.photo_links?.length > 0 && (
                        <div className={styles.uploadedFiles}>
                            <p className={styles.uploadedFilesTitle}>
                                Uploaded files
                            </p>
                            <div className={styles.uploadedFilesList}>
                                {(
                                    client[ClientStatus.INS_FOLLOWUP]?.photo_links ||
                                    []
                                ).map((link, idx) => {
                                    return (
                                        <span
                                            key={`ins-card-${idx}`}
                                            className={styles.uploadedFile}
                                        >
                                            <a
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                href={link.url}
                                                onClick={(event) =>
                                                    openInsuranceCard(
                                                        event,
                                                        link.url
                                                    )
                                                }
                                                className={styles.uploadedFileLink}
                                            >
                                                {link.name}
                                            </a>
                                            {allowUpload && (
                                                <Button
                                                    variant="normal"
                                                    onClick={() =>
                                                        removeInsurancePhotos(link)
                                                    }
                                                    className={
                                                        styles.uploadedFileRemove
                                                    }
                                                >
                                                    <CancelIcon stroke="#000000" />
                                                </Button>
                                            )}
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    )}
            </div>
        </Card>
    );
}
